//
import type { Priority } from '@/types/Models/Priority'
import type { PriorityStoreState } from '@/types/Store'
import { defineStore } from 'pinia'
import { ASSIGNMENT, CUSTOMER, SENDER_ADDRESS } from '@/const/PriorityTypes'
import { unionBy } from '@/services/Utils'
import BasePriorityProxy from '@/proxies/Priority/BasePriorityProxy'
import CompanyPriorityProxy from '@/proxies/Priority/CompanyPriorityProxy'
import AssignmentPriorityProxy from '@/proxies/Priority/AssignmentPriorityProxy'
import SenderAddressPriorityProxy from '@/proxies/Priority/SenderAddressPriorityProxy'

const proxyByType = (type: string) => {
  switch (type) {
    case CUSTOMER:
      return new CompanyPriorityProxy()
    case ASSIGNMENT:
      return new AssignmentPriorityProxy()
    case SENDER_ADDRESS:
      return new SenderAddressPriorityProxy()
    default:
      return new BasePriorityProxy()
  }
}

export const usePriorityStore = defineStore('priority', {
  state: (): PriorityStoreState => ({
    priorities: [],
    changed: [],
    type: '',
  }),
  actions: {
    setType(type: string) {
      this.type = type
    },
    updatePriorities(priorities: Priority[]) {
      this.priorities = unionBy(priorities, this.priorities, 'id')
      this.changed = unionBy(priorities, this.changed, 'id')
    },
    resetState() {
      this.changed = []
      this.priorities = []
      this.type = ''
    },
    set(priorities: Priority[]) {
      this.updatePriorities(priorities)
    },
    reset(id: string) {
      const proxy = proxyByType(this.type)

      const resetPromise =
        this.type === CUSTOMER || this.type === ASSIGNMENT || this.type === SENDER_ADDRESS
          ? proxy.destroy(id)
          : Promise.resolve()

      return resetPromise.then(() => {
        this.resetState()
      })
    },
    fetch(id = '') {
      const proxy = proxyByType(this.type)
      const request = this.type === '' ? (proxy as BasePriorityProxy).all() : proxy.find(id)

      return request.then((result) => {
        this.priorities = result.data
        return result.data
      })
    },
    save(id: string) {
      const proxy = proxyByType(this.type)
      return proxy.update(id, { priorities: this.priorities }).then(() => {
        this.resetState()
      })
    },
  },
})

export type PriorityStore = ReturnType<typeof usePriorityStore>
